/**
 * Button Component
 *
 * Standardized button for Admin UI consistency.
 * Supports variants: primary, secondary, ghost, danger, success, warning
 * Supports sizes: lg (large), default/md (medium), sm (small)
 *
 * @package PresszoneComments
 */

import { el, __ } from '../utils/dom.js';

/**
 * Create a standardized button element.
 *
 * @param {Object} options - Button options
 * @param {string} options.label - Button text
 * @param {string} [options.variant='primary'] - Button style: primary, secondary, ghost, danger, success, warning
 * @param {string} [options.size='default'] - Button size: lg, default/md, sm
 * @param {string} [options.padding] - Button padding: wide, standard, minimal
 * @param {string} [options.styling] - Button styling: square, rounded, pill
 * @param {string} [options.type='button'] - Button type: button, submit
 * @param {Function} [options.onClick] - Click handler
 * @param {boolean} [options.disabled=false] - Disabled state
 * @param {string} [options.icon] - Optional icon (emoji or text)
 * @param {string} [options.title] - Optional tooltip
 * @param {Object} [options.attrs] - Additional attributes
 * @returns {HTMLButtonElement}
 */
export function Button({
    label,
    variant = 'primary',
    size = 'default',
    padding,
    styling,
    type = 'button',
    onClick,
    disabled = false,
    icon,
    title,
    attrs = {}
} = {}) {
    const classes = ['presszone-comments-btn'];

    // Add variant class
    if (variant && variant !== 'default') {
        classes.push(`presszone-comments-btn--${variant}`);
    }

    // Add size class (lg, default/md is medium, sm)
    if (size === 'lg') {
        classes.push('presszone-comments-btn--lg');
    } else if (size === 'sm') {
        classes.push('presszone-comments-btn--sm');
    }
    // 'default' and 'md' use base styles (no size class needed)

    // Add padding variant class
    if (padding) {
        classes.push(`presszone-comments-btn--padding-${padding}`);
    }

    // Add styling (border radius) variant class
    if (styling) {
        classes.push(`presszone-comments-btn--styling-${styling}`);
    }

    const buttonAttrs = {
        type,
        class: classes.join(' '),
        disabled: disabled || undefined,
        onclick: onClick,
        title: title || undefined,
        ...attrs
    };

    // Build button content
    if (icon && label) {
        return el('button', buttonAttrs, icon + ' ' + label);
    } else if (icon) {
        return el('button', buttonAttrs, icon);
    } else {
        return el('button', buttonAttrs, label);
    }
}

/**
 * Create a Cancel button (secondary/ghost variant).
 */
export function CancelButton(onClick, options = {}) {
    return Button({
        label: __('Cancel', 'comments-press-zone'),
        variant: 'ghost',
        onClick,
        ...options
    });
}

/**
 * Create a Save button (primary variant).
 */
export function SaveButton(label, onClick, disabled = false, options = {}) {
    return Button({
        label: label || __('Save', 'comments-press-zone'),
        variant: 'primary',
        onClick,
        disabled,
        ...options
    });
}

/**
 * Create a Delete button (danger variant).
 */
export function DeleteButton(onClick, label, options = {}) {
    return Button({
        label: label || __('Delete', 'comments-press-zone'),
        variant: 'danger',
        onClick,
        ...options
    });
}

export default Button;
